package uz.mint.frontend

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color

/**
 * The design's token set from *Mint Landing.dc.html*, one value per [ColorMode].
 *
 * The design ships these as CSS custom properties switched by `html[data-theme]`; Silk already
 * owns a light/dark switch with its own persistence, so they live here as Kotlin instead of a
 * second theming mechanism fighting the first.
 */
class SitePalette(
    /** Brand green. Identical in both modes — it is the one fixed point of the identity. */
    val mint: Color,
    val mintHover: Color,
    val mintStrong: Color,
    /** Pale green wash behind hovered surfaces. */
    val tint: Color,
    /** Primary text. */
    val ink: Color,
    /** Secondary text. */
    val ink2: Color,
    /** Page background. */
    val paper: Color,
    /** Alternating section background. */
    val paper2: Color,
    /** Card surface. */
    val card: Color,
    /** The 2px hairline the whole layout is drawn with. */
    val rule: Color,
    /** Dark band used by the promo bar and footer. */
    val band: Color,
    val bandFg: Color,
    val bandLine: Color,
    val bandBorder: Color,
    /** Small-caps accent text. */
    val accentText: Color,
    val icon: Color,
    /** Text on a mint fill. */
    val onMint: Color,
    /** High-contrast solid button. Inverts between modes. */
    val solidBg: Color,
    val solidFg: Color,
    val solidHover: Color,
    /** The lockup SVG has a baked-in fill rather than `currentColor`, so each mode needs its own file. */
    val lockup: String,
)

object SitePalettes {
    val light = SitePalette(
        mint = Color.rgb(0x6ED858),
        mintHover = Color.rgb(0x4FC138),
        mintStrong = Color.rgb(0x3AA627),
        tint = Color.rgb(0xEDFBE8),
        ink = Color.rgb(0x0F2620),
        ink2 = Color.rgb(0x1B3A31),
        paper = Color.rgb(0xFFFFFF),
        paper2 = Color.rgb(0xF4F6F2),
        card = Color.rgb(0xFFFFFF),
        rule = Color.rgb(0x0F2620),
        band = Color.rgb(0x0F2620),
        bandFg = Color.rgb(0xDCE8D8),
        bandLine = Color.rgba(255, 255, 255, 0.18f),
        bandBorder = Color.rgba(255, 255, 255, 0.28f),
        accentText = Color.rgb(0x2E7D1F),
        icon = Color.rgb(0x3AA627),
        onMint = Color.rgb(0x0F2620),
        solidBg = Color.rgb(0x0F2620),
        solidFg = Color.rgb(0xFFFFFF),
        solidHover = Color.rgb(0x1B3A31),
        lockup = "/mint-lockup-ink.svg",
    )

    val dark = SitePalette(
        mint = Color.rgb(0x6ED858),
        mintHover = Color.rgb(0x4FC138),
        mintStrong = Color.rgb(0x3AA627),
        tint = Color.rgb(0x193227),
        ink = Color.rgb(0xE8F2E5),
        ink2 = Color.rgb(0xA7C2B6),
        paper = Color.rgb(0x0C1A16),
        paper2 = Color.rgb(0x12251F),
        card = Color.rgb(0x12251F),
        rule = Color.rgb(0x2C4A40),
        band = Color.rgb(0x071310),
        bandFg = Color.rgb(0xDCE8D8),
        bandLine = Color.rgba(255, 255, 255, 0.14f),
        bandBorder = Color.rgba(255, 255, 255, 0.22f),
        accentText = Color.rgb(0x8BDC74),
        icon = Color.rgb(0x6ED858),
        onMint = Color.rgb(0x0F2620),
        solidBg = Color.rgb(0xE8F2E5),
        solidFg = Color.rgb(0x0C1A16),
        solidHover = Color.rgb(0xFFFFFF),
        lockup = "/mint-lockup-white.svg",
    )
}

fun ColorMode.toSitePalette(): SitePalette = when (this) {
    ColorMode.LIGHT -> SitePalettes.light
    ColorMode.DARK -> SitePalettes.dark
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.background = SitePalettes.light.paper
    ctx.theme.palettes.light.color = SitePalettes.light.ink
    ctx.theme.palettes.dark.background = SitePalettes.dark.paper
    ctx.theme.palettes.dark.color = SitePalettes.dark.ink
}
